import { Transforms, Editor, Path, NodeEntry, Node, Element } from 'slate';
import { isListItem, isList, listItemType } from './listType';

export const isValidListItemContent = (
  node: Node,
  editor: Editor
): node is Element =>
  Element.isElement(node) &&
  !Element.isElementList(node.children) &&
  !editor.isVoid(node);

const isListItemWithValidContent = (
  node: Node,
  editor: Editor
): node is Element =>
  isListItem(node) &&
  node.children.length === 1 &&
  isValidListItemContent(node.children[0], editor);

const isListItemWithDenormalizedInvalidContent = (
  node: Node,
  editor: Editor
): node is Element =>
  isListItem(node) &&
  (node.children.length === 1
    ? isList(node.children[0]) || isListItem(node.children[0])
    : !isListItemWithValidContent(node, editor));

const flattenListItemWithDenormalizedInvalidContent = (
  invalidListItem: Element
): Node[] => {
  let nodes = new Array<Node>();

  invalidListItem.children.forEach((child) => {
    if (isList(child)) {
      nodes = [...nodes, ...child.children];
    } else {
      nodes.push(child);
    }
  });

  return nodes.map((node) =>
    isListItem(node)
      ? node
      : {
          type: listItemType,
          children: [node],
        }
  );
};

/**
 * Normalises any list items that are invalid because they have unexpected or multiple content blocks as children.
 * Partially normalizes them by making each child be its own list item in the parent list.
 * The next function in the normalization chain will then "breakout" any content which is not valid to be nested inside a list item.
 */
const partiallyNormalizeAnyListItemWithInvalidContent = (
  listElement: Element,
  listPath: Path,
  editor: Editor
): boolean => {
  const invalidMatch = listElement.children
    .map((node, index) => ({ node, index }))
    .find((child) =>
      isListItemWithDenormalizedInvalidContent(child.node, editor)
    );

  if (invalidMatch && Element.isElement(invalidMatch.node)) {
    const currentListItem = invalidMatch.node;
    const currentListItemPath = [...listPath, invalidMatch.index];

    // Saving node and offset pre-transformations to reposition the cursor
    const [node] = Editor.node(editor, editor.selection?.focus.path || []);
    const offset = editor.selection?.focus.offset || 0;

    // Remove the current (invalid) list item
    Transforms.removeNodes(editor, { at: currentListItemPath });

    // Enumerate the content to be brought up to parent list as sepearate list items
    const flattenedContent = flattenListItemWithDenormalizedInvalidContent(
      currentListItem
    );

    // Insert all of the children of the invalid list in to our parent list, above the "current list item"
    Transforms.insertNodes(editor, flattenedContent, {
      at: currentListItemPath,
    });

    // Repositioning cursor
    const [listNode] = Editor.node(editor, listPath);
    for (const [findNode, findPathRelative] of Node.descendants(listNode)) {
      if (findNode === node) {
        Transforms.select(editor, {
          path: [...listPath, ...findPathRelative],
          offset,
        });
        break;
      }
    }

    return true;
  }

  return false;
};

/**
 * Breakout (move outside of the list) any content which is not valid to be nested inside a list item
 */
const findAndFixAnyListItemWithInvalidContent = (
  listElement: Element,
  path: Path,
  editor: Editor
): boolean => {
  const invalidMatch = listElement.children
    .map((node, index) => ({ node, index }))
    .find(
      (child) =>
        isListItem(child.node) &&
        !isListItemWithValidContent(child.node, editor)
    );

  if (invalidMatch && Element.isElement(invalidMatch.node)) {
    const currentListItem = invalidMatch.node;
    const currentListItemPath = [...path, invalidMatch.index];
    const invalidChild = currentListItem.children[0];

    // Remove the list item that contains the invalid content
    // (note because of the match in the find above, we know the list item only contains a single child, so the whole item is safe to remove)
    Transforms.removeNodes(editor, { at: currentListItemPath });

    // If that would leave the list empty, remove that too
    if (listElement.children.length === 1) {
      Transforms.removeNodes(editor, { at: path });
    }

    // Split the list if the invalid content is inbetween the start and end (i.e. at neither end)
    // And insert the invalid content at the visual position of where the invalid list item was
    let requiresSplit = false;
    let nudge = 0;

    if (invalidMatch.index === 0) {
      requiresSplit = false;
      nudge = 0;
    } else if (invalidMatch.index === listElement.children.length - 1) {
      requiresSplit = false;
      nudge = 1;
    } else {
      requiresSplit = true;
      nudge = 1;
    }

    if (requiresSplit) {
      Transforms.splitNodes(editor, { at: currentListItemPath });
    }

    const finalInsertionPath = [...path];
    finalInsertionPath[finalInsertionPath.length - 1] += nudge;

    Transforms.insertNodes(editor, invalidChild, { at: finalInsertionPath });

    return true;
  }

  return false;
};

export const listNormalizer = ({
  entry,
  editor,
  normalizeNode,
}: {
  entry: NodeEntry<Node>;
  editor: Editor;
  normalizeNode: (entry: NodeEntry<Node>) => void;
}) => {
  const [node, path] = entry;

  // Handle fixing invalid content inside of list items
  if (isList(node)) {
    // Handle invalid list items that have unexpected/multiple content blocks directly inside them, and if present,
    // partially fix (by separating each block as an individual list item), and return to start another normalization run.
    if (partiallyNormalizeAnyListItemWithInvalidContent(node, path, editor)) {
      return;
    }

    // Breakout (move outside of the list) any content which is not valid to be nested inside a list item, and if present, return.
    if (findAndFixAnyListItemWithInvalidContent(node, path, editor)) {
      return;
    }
  }

  // Handle merging of lists, but only at the top-level
  if (isList(node) && path.length === 1) {
    // Handle merging list with previous sibling list of the same type
    if (Path.hasPrevious(path)) {
      const [[prevSibling, prevSiblingPath]] = Editor.nodes(editor, {
        mode: 'highest',
        at: Path.previous(path),
        match: (match) => !Editor.isEditor(match),
      });

      if (isList(prevSibling) && node.type === prevSibling.type) {
        const listItemEntries = Editor.nodes(editor, {
          at: path,
          match: isListItem,
        });

        Transforms.insertNodes(
          editor,
          Array.from(listItemEntries).map(([listItem]) => listItem),
          {
            at: [...prevSiblingPath, prevSibling.children.length],
            select: true,
          }
        );

        Transforms.removeNodes(editor, { at: path });

        return;
      }
    }

    // Handle merging list with next sibling list of the same type
    const next = Path.next(path);
    if (next[0] < editor.children.length) {
      const [[nextSibling, nextSiblingPath]] = Editor.nodes(editor, {
        mode: 'highest',
        at: next,
        match: (match) => !Editor.isEditor(match),
      });

      if (isList(nextSibling) && node.type === nextSibling.type) {
        const listItemEntries = Editor.nodes(editor, {
          at: path,
          match: isListItem,
        });

        Transforms.insertNodes(
          editor,
          Array.from(listItemEntries).map(([listItem]) => listItem),
          {
            at: [...nextSiblingPath, 0],
            select: true,
          }
        );

        Transforms.removeNodes(editor, { at: path });

        return;
      }
    }
  }

  normalizeNode(entry);
};
